import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { Character } from '../contexts/AuthContext';
import { getOrCreateGuestCharacterId } from '../utils/guestSession';
import { WhatIsGrandLineModal } from './WhatIsGrandLineModal';
import { LoginWorldCanvas } from './LoginWorldCanvas';

// CSS keyframes for animations (injected once)
const injectStyles = (): void => {
  if (document.getElementById('login-screen-animations')) return;
  const style = document.createElement('style');
  style.id = 'login-screen-animations';
  style.textContent = `
    @keyframes loginSpinner {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes loginPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes loginFadeIn {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes loginFadeOut {
      from { opacity: 1; transform: translateY(0); }
      to { opacity: 0; transform: translateY(8px); }
    }
    @keyframes loginSlideIn {
      from { opacity: 0; transform: translateX(-10px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes loginShake {
      0%, 100% { transform: translateX(0); }
      20%, 60% { transform: translateX(-4px); }
      40%, 80% { transform: translateX(4px); }
    }
    @keyframes loginSuccess {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    @keyframes loginGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(88, 166, 255, 0); }
      50% { box-shadow: 0 0 8px 2px rgba(88, 166, 255, 0.4); }
    }
    @keyframes loginShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes loginLogoRock {
      0%, 100% { transform: rotate(0deg); }
      25% { transform: rotate(2deg); }
      50% { transform: rotate(0deg); }
      75% { transform: rotate(-2deg); }
    }

    /* Grid-based background to match in-game aesthetic */
    @keyframes loginGridDrift {
      0% { background-position: 0 0, 0 0; }
      100% { background-position: 32px 16px, -16px 8px; }
    }

    /* Subtle drift for faux islands */
    @keyframes loginIslandDrift {
      0% { transform: translate3d(0px, 0px, 0); }
      100% { transform: translate3d(-12px, 8px, 0); }
    }

    .login-screen {
      position: relative;
      overflow: hidden;
      background: #0b1220;
    }

    /* Grid layers (sharp, square, no organic shapes) */
    .login-screen::before {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 0;
      background-image:
        /* Major grid every 64px */
        linear-gradient(to right, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
        /* Minor grid every 16px */
        linear-gradient(to right, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
      background-size:
        64px 64px,
        64px 64px,
        16px 16px,
        16px 16px;
      opacity: 0.85;
      animation: loginGridDrift 14s linear infinite;
    }

    .highlight {
      color: #22c55e;
      text-shadow: 0 0 8px rgba(34, 197, 94, 0.6), 0 0 16px rgba(34, 197, 94, 0.3);
      font-weight: 700;
    }

  `;
  document.head.appendChild(style);
};

const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0d1117',
        border: '1px solid #30363d',
        color: '#e6edf3',
        cursor: 'pointer',
        fontFamily: 'var(--font-mono)',
        fontSize: 16,
        lineHeight: 1,
      }}
      aria-label="Close"
      title="Close"
    >
      ×
    </button>
  );
};

interface LoginScreenProps {
  onJoin: (
    name: string,
    character: string,
    shipColor: string,
    characterId?: string,
    accountId?: string,
    savedPosition?: { x: number; y: number },
    savedStats?: { berries: number; level: number; xp: number; infamy: number; signatureMoves?: unknown[]; equippedSignatureMove?: string | null },
    savedSkills?: unknown,
    startingOcean?: string,
    token?: string
  ) => void;
  onNavigateToPrivacy: () => void;
  onNavigateToTerms: () => void;
}

// Starting ocean options - matches server WORLD_REGIONS
const STARTING_OCEANS: Array<{ id: string; name: string; description: string; difficulty: string; color: string }> = [
  { id: 'eastern_reach', name: 'Eastern Reach', description: 'Calm waters, ideal for beginners', difficulty: 'Easy', color: '#22c55e' },
  { id: 'southern_reach', name: 'Southern Reach', description: 'Mixed threats, Navy patrols', difficulty: 'Medium', color: '#eab308' },
  { id: 'western_reach', name: 'Western Reach', description: 'Smuggler territory, moderate danger', difficulty: 'Medium', color: '#f97316' },
  { id: 'northern_reach', name: 'Northern Reach', description: 'Dangerous waters, experienced sailors', difficulty: 'Hard', color: '#ef4444' },
];

const CHARACTERS: Array<{ 
  id: string; 
  name: string; 
  portraitPath: string;
  startingReputation: { faction: string; value: number }[];
  startingBonuses?: { berries?: number; xp?: number };
}> = [
  { 
    id: 'pirate', 
    name: 'Pirate',
    portraitPath: '/assets/portraits/pirates/pirate_1.png',
    startingReputation: [
      { faction: 'corsairs', value: 500 },
      { faction: 'navy', value: -250 },
    ],
    startingBonuses: { berries: 500 }
  },
  { 
    id: 'marine', 
    name: 'Marine',
    portraitPath: '/assets/portraits/marines/marine_1.png',
    startingReputation: [
      { faction: 'navy', value: 500 },
      { faction: 'corsairs', value: -250 },
    ],
    startingBonuses: { berries: 500 }
  },
  { 
    id: 'merchant', 
    name: 'Merchant',
    portraitPath: '/assets/portraits/pirates/pirate_5.png',
    startingReputation: [
      { faction: 'merchant_guild', value: 500 },
    ],
    startingBonuses: { berries: 1000 }
  },
];

const SHIP_COLORS: Array<{ id: string; hex: string; name: string }> = [
  { id: 'red', hex: '#e74c3c', name: 'Crimson' },
  { id: 'orange', hex: '#e67e22', name: 'Orange' },
  { id: 'yellow', hex: '#f1c40f', name: 'Gold' },
  { id: 'green', hex: '#2ecc71', name: 'Emerald' },
  { id: 'teal', hex: '#1abc9c', name: 'Teal' },
  { id: 'blue', hex: '#3498db', name: 'Azure' },
  { id: 'purple', hex: '#9b59b6', name: 'Violet' },
  { id: 'pink', hex: '#e91e63', name: 'Rose' },
];

interface ServerStatus {
  status: 'online' | 'offline';
  explorerCount?: number;
}

type AuthPanelMode = 'closed' | 'login' | 'signup';

// Loading spinner component
const LoadingSpinner: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#58a6ff' }) => (
  <div style={{
    width: size,
    height: size,
    border: `2px solid ${color}33`,
    borderTop: `2px solid ${color}`,
    animation: 'loginSpinner 0.8s linear infinite',
    display: 'inline-block',
  }} />
);

// Skeleton loader for placeholder content
interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
}

const Skeleton: React.FC<SkeletonProps> = ({ width = '100%', height = 16, style }) => (
  <div style={{
    width,
    height,
    background: 'linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%)',
    backgroundSize: '200% 100%',
    animation: 'loginShimmer 1.5s ease-in-out infinite',
    ...style,
  }} />
);

// Character card skeleton
const CharacterSkeleton: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    background: '#111827',
    border: '1px solid #1f2937',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <Skeleton width={24} height={24} />
      <div>
        <Skeleton width={100} height={14} style={{ marginBottom: 6 }} />
        <Skeleton width={140} height={11} />
      </div>
    </div>
    <Skeleton width={60} height={28} />
  </div>
);


// Toast notification component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

// Footer links component - reusable across all auth states
interface FooterLinksProps {
  onShowWhatIs: () => void;
  onShowDailyRewards: () => void;
  isLoggedIn: boolean;
  serverStatus?: ServerStatus | null;
}

const FooterLinks: React.FC<FooterLinksProps> = ({ onShowWhatIs, onShowDailyRewards, isLoggedIn, serverStatus }) => (
  <footer style={{
    position: 'fixed',
    bottom: '16px',
    left: '0',
    right: '0',
    zIndex: 50,
    pointerEvents: 'auto',
    textAlign: 'center',
  }}>
    <div style={{
      margin: '0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '0',
      padding: '8px 12px',
      background: 'rgba(13, 17, 23, 0.65)',
      border: '1px solid rgba(33, 38, 45, 0.75)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRadius: '6px',
      fontSize: '11px',
      color: '#6b7280',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
    }}>
      {serverStatus?.status === 'online' && (
        <>
          <span style={{ color: '#22c55e', fontWeight: 'bold', padding: '4px 8px' }}>
            ● {serverStatus.explorerCount || 0} explorers active
          </span>
          <span style={{ margin: '0 4px' }}>•</span>
        </>
      )}
      <button
        onClick={onShowWhatIs}
        style={{
          background: 'none',
          border: 'none',
          color: '#f0883e',
          cursor: 'pointer',
          fontSize: '11px',
          padding: '4px 8px',
        }}
      >
        What is Grand Online?
      </button>
      <span style={{ margin: '0 4px' }}>•</span>
      <a
        href="/privacy"
        style={{
          color: '#58a6ff',
          cursor: 'pointer',
          fontSize: '11px',
          padding: '4px 8px',
          textDecoration: 'none',
        }}
      >
        Privacy Policy
      </a>
      <span style={{ margin: '0 4px' }}>•</span>
      <a
        href="/terms"
        style={{
          color: '#58a6ff',
          cursor: 'pointer',
          fontSize: '11px',
          padding: '4px 8px',
          textDecoration: 'none',
        }}
      >
        Terms and Conditions
      </a>
      <span style={{ margin: '0 4px' }}>•</span>
      <a
        href="/hiscores"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#f0883e',
          fontSize: '11px',
          padding: '4px 8px',
          textDecoration: 'none',
          fontWeight: 'bold',
        }}
      >
        Hiscores
      </a>
    </div>
  </footer>
);

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const colors = {
    success: { bg: '#064e3b', border: '#10b981', text: '#34d399' },
    error: { bg: '#3d1a1a', border: '#f85149', text: '#f87171' },
    info: { bg: '#1e3a5f', border: '#3b82f6', text: '#60a5fa' },
  };
  const c = colors[type];

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: c.bg,
      border: `1px solid ${c.border}`,
      color: c.text,
      padding: '10px 16px',
      fontSize: '12px',
      fontFamily: '"Sometype Mono", monospace',
      zIndex: 1000,
      animation: 'loginFadeIn 0.3s ease-out',
    }}>
      {message}
    </div>
  );
};

export const LoginScreen: React.FC<LoginScreenProps> = ({ onJoin, onNavigateToPrivacy, onNavigateToTerms }) => {
  const auth = useAuth();

  const isInIframe: boolean = (() => {
    try {
      return window.self !== window.top;
    } catch (_error) {
      return true;
    }
  })();

  const isOAuthDisabledInIframe: boolean = process.env.REACT_APP_DISABLE_OAUTH_IN_IFRAME === 'true' && isInIframe;
  const shouldShowOAuthButtons: boolean = !isOAuthDisabledInIframe;

  const [authPanelMode, setAuthPanelMode] = useState<AuthPanelMode>('closed');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [character, setCharacter] = useState<string>('pirate');
  const [shipColor, setShipColor] = useState<string>('#3498db');
  const [startingOcean, setStartingOcean] = useState<string>('eastern_reach');
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [isCreatingCharacter, setIsCreatingCharacter] = useState<boolean>(false);
  const [isSubmittingCharacter, setIsSubmittingCharacter] = useState<boolean>(false);
  const [isSigningIn, setIsSigningIn] = useState<boolean>(false);
  const [isSigningOut, setIsSigningOut] = useState<boolean>(false);
  const [deletingCharId, setDeletingCharId] = useState<string | null>(null);
  const [playingCharId, setPlayingCharId] = useState<string | null>(null);
  
  // Toast notifications
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  
  // What is Grand Line modal
  const [showWhatIsModal, setShowWhatIsModal] = useState<boolean>(false);
  
  // Ocean selection screen (shown after clicking Set Sail)
  const [showOceanSelection, setShowOceanSelection] = useState<boolean>(false);
  
  // Username claim modal
  const [showUsernameClaimModal, setShowUsernameClaimModal] = useState<boolean>(false);
  
  
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // Character name availability
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [nameCheckError, setNameCheckError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState<boolean>(false);
  
  // Username claim
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [claimUsername, setClaimUsername] = useState<string>('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState<boolean>(false);
  const [isClaimingUsername, setIsClaimingUsername] = useState<boolean>(false);
  
  // Inject CSS animations on mount
  useEffect(() => {
    injectStyles();
  }, []);
  
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  }, []);

  // Fetch server status on mount and periodically
  useEffect(() => {
    const statusUrl = process.env.REACT_APP_API_URL || window.location.origin || 'http://localhost:3001';

    const fetchStatus = async (): Promise<void> => {
      try {
        const res = await fetch(`${statusUrl}/api/status`);
        if (res.ok) {
          const data = await res.json();
          setServerStatus({
            status: data.status === 'online' ? 'online' : 'offline',
            explorerCount: data.explorerCount || 0,
          });
        } else {
          setServerStatus({ status: 'offline' });
        }
      } catch {
        // Server might not be running yet
        setServerStatus({ status: 'offline' });
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  // Close auth panel when user logs in
  useEffect(() => {
    if (auth.user) {
      setAuthPanelMode('closed');
    }
  }, [auth.user]);

  // Show username claim modal if user is logged in but has no username
  // Only show once per session; don't reopen if user dismissed it
  useEffect(() => {
    if (auth.user && auth.accountInfo && !auth.accountInfo.username) {
      // Only auto-open if this is the first time we're seeing this user without a username
      // Check if we've already shown the modal in this session
      const hasShownModal = sessionStorage.getItem('usernameModalShown');
      if (!hasShownModal) {
        setShowUsernameClaimModal(true);
        sessionStorage.setItem('usernameModalShown', 'true');
      }
    }
  }, [auth.user, auth.accountInfo]);

  // Debounced username availability check
  useEffect(() => {
    if (!claimUsername || claimUsername.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    const usernameRegex = /^[a-zA-Z0-9_ ]{3,20}$/;
    if (!usernameRegex.test(claimUsername)) {
      setUsernameAvailable(null);
      setUsernameError('3-20 characters, letters, numbers, underscores, spaces only');
      return;
    }

    setUsernameError(null);
    setIsCheckingUsername(true);

    const timer = setTimeout(async () => {
      try {
        const available = await auth.checkUsernameAvailable(claimUsername);
        setUsernameAvailable(available);
        setUsernameError(null);
      } catch (err) {
        setUsernameAvailable(null);
        setUsernameError(err instanceof Error ? err.message : 'Failed to check availability');
      } finally {
        setIsCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [claimUsername, auth]);

  // Debounced character name availability check
  useEffect(() => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameAvailable(null);
      setNameCheckError(null);
      return;
    }
    if (trimmed.length > 20) {
      setNameAvailable(false);
      setNameCheckError('Max 20 characters');
      return;
    }

    setIsCheckingName(true);
    setNameCheckError(null);
    const timer = setTimeout(async () => {
      try {
        const result = await auth.checkCharacterNameAvailable(trimmed);
        setNameAvailable(result.available);
        setNameCheckError(result.error || null);
      } catch (err) {
        setNameAvailable(false);
        setNameCheckError(err instanceof Error ? err.message : 'Failed to check availability');
      } finally {
        setIsCheckingName(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [name, auth]);

  const handleClaimUsername = async (): Promise<void> => {
    if (!claimUsername || !usernameAvailable) return;

    setIsClaimingUsername(true);
    setUsernameError(null);

    const result = await auth.claimUsername(claimUsername);

    if (result.success) {
      showToast(`Username "${claimUsername}" claimed successfully!`, 'success');
      setShowUsernameClaimModal(false);
      setClaimUsername('');
    } else {
      setUsernameError(result.error || 'Failed to claim username');
    }

    setIsClaimingUsername(false);
  };

  const handleGuestSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (name.trim()) {
      setShowOceanSelection(true);
    }
  };
  
  const handleOceanConfirm = (): void => {
    const guestCharacterId: string = getOrCreateGuestCharacterId();
    // Use entered name if available, otherwise generate a guest name
    const shipName = name.trim() || `Guest_${Math.random().toString(36).slice(2, 8)}`;
    console.log('Ocean confirmed - joining game with:', { shipName, character, shipColor, guestCharacterId, startingOcean });
    onJoin(shipName, character, shipColor, guestCharacterId, undefined, undefined, undefined, undefined, startingOcean, undefined);
  };

  const handleAuthSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    auth.clearError();
    setIsSigningIn(true);

    try {
      if (authPanelMode === 'login') {
        const success = await auth.signIn(email, password);
        if (success) {
          showToast('Signed in successfully!', 'success');
        }
      } else if (authPanelMode === 'signup') {
        const success = await auth.signUp(email, password);
        if (success) {
          showToast('Account created! Check your email to verify.', 'success');
        }
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    auth.clearError();
    setIsSigningIn(true);
    showToast('Redirecting to Google...', 'info');
    await auth.signInWithGoogle();
    // Note: page will redirect, so no need to reset loading
  };

  const handleDiscordSignIn = async (): Promise<void> => {
    auth.clearError();
    setIsSigningIn(true);
    showToast('Redirecting to Discord...', 'info');
    await auth.signInWithDiscord();
    // Note: page will redirect, so no need to reset loading
  };

  const handlePlayCharacter = (char: Character): void => {
    setPlayingCharId(char.id);
    showToast(`Setting sail as ${char.name}...`, 'info');
    // Pass saved position if character has valid coordinates
    const savedPosition = (char.x > 0 && char.y > 0) ? { x: char.x, y: char.y } : undefined;
    // Pass saved stats (berries, level, xp, infamy, signature moves)
    const charWithMoves = char as unknown as { 
      signature_moves?: unknown[]; 
      equipped_signature_move?: string | null;
    };
    const savedStats = {
      berries: char.berries || 1000,
      level: char.level || 1,
      xp: char.experience || 0,
      infamy: char.infamy || 0,
      signatureMoves: charWithMoves.signature_moves || [],
      equippedSignatureMove: charWithMoves.equipped_signature_move || null,
    };
    // Pass saved skills snapshot from characters.skills JSONB
    const savedSkills: unknown = char.skills ?? undefined;
    // Small delay for visual feedback
    setTimeout(() => {
      onJoin(char.name, char.characterType, char.shipColor, char.id, char.accountId, savedPosition, savedStats, savedSkills, startingOcean, auth.session?.access_token);
    }, 300);
  };

  const handleCreateCharacter = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim() || nameAvailable === false || isCheckingName) return;

    setIsSubmittingCharacter(true);
    try {
      const newChar = await auth.createCharacter(name.trim(), character, shipColor);
      if (newChar) {
        showToast(`${newChar.name} created successfully!`, 'success');
        setIsCreatingCharacter(false);
        setIsSubmittingCharacter(false);
        setName('');
        setNameAvailable(null);
        setNameCheckError(null);
      } else {
        showToast('Failed to create character', 'error');
      }
    } finally {
      setIsCreatingCharacter(false);
      setIsSubmittingCharacter(false);
    }
  };

  const handleSignOut = async (): Promise<void> => {
    setIsSigningOut(true);
    try {
      await auth.signOut();
      showToast('Signed out successfully', 'success');
      setEmail('');
      setPassword('');
    } finally {
      setIsSigningOut(false);
    }
  };
  
  const handleDeleteCharacter = async (charId: string, charName: string): Promise<void> => {
    setDeletingCharId(charId);
    try {
      const success = await auth.deleteCharacter(charId);
      if (success) {
        showToast(`${charName} has been deleted`, 'success');
      } else {
        showToast('Failed to delete character', 'error');
      }
    } finally {
      setDeletingCharId(null);
      setDeleteConfirmId(null);
    }
  };

  // Auth panel component (top-right dropdown)
  const renderAuthPanel = (): React.ReactElement | null => {
    if (!auth.isAuthEnabled) return null;

    // User is logged in - show account info
    if (auth.user) {
      const statusColors: Record<string, string> = {
        user: '#8b949e',
        moderator: '#f0883e',
        admin: '#f85149',
      };
      const statusLabel = auth.accountInfo?.status || 'user';
      const showStatusLabel = statusLabel === 'moderator' || statusLabel === 'admin';
      const isAccountInfoLoading = auth.isAccountInfoLoading || (!auth.accountInfo && auth.user);

      return (
        <div
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(13, 17, 23, 0.72)',
            border: '1px solid rgba(33, 38, 45, 0.8)',
            padding: '12px',
            minWidth: '220px',
            zIndex: 100,
            fontFamily: 'var(--font-mono)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '6px',
            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
          }}
        >
          {/* Username display */}
          {isAccountInfoLoading ? (
            <div
              style={{
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '11px',
                color: '#8b949e',
              }}
            >
              <LoadingSpinner size={12} color="#8b949e" />
              <span>Loading account...</span>
            </div>
          ) : auth.accountInfo?.username ? (
            <div style={{ marginBottom: '8px' }}>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#58a6ff',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {auth.accountInfo.username}
              </div>
              {showStatusLabel && (
                <div
                  style={{
                    fontSize: '10px',
                    color: statusColors[statusLabel],
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {statusLabel}
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowUsernameClaimModal(true)}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '8px',
                background: '#1a1a2e',
                border: '1px dashed #58a6ff',
                color: '#58a6ff',
                cursor: 'pointer',
                fontSize: '11px',
              }}
            >
              + CLAIM USERNAME
            </button>
          )}

          <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>Signed in as</div>
          <div
            style={{
              fontSize: '12px',
              color: '#e2e8f0',
              marginBottom: '8px',
              wordBreak: 'break-all',
            }}
          >
            {auth.user.email}
          </div>
          <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            style={{
              width: '100%',
              padding: '6px 10px',
              background: '#21262d',
              border: '1px solid #30363d',
              color: '#e2e8f0',
              cursor: isSigningOut ? 'wait' : 'pointer',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              opacity: isSigningOut ? 0.7 : 1,
            }}
          >
            {isSigningOut && <LoadingSpinner size={12} color="#e2e8f0" />}
            {isSigningOut ? 'SIGNING OUT...' : 'SIGN OUT'}
          </button>
        </div>
      );
    }

    // Auth panel closed - show sign in button
    if (authPanelMode === 'closed') {
      return (
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          gap: '8px',
          zIndex: 100,
        }}>
          <button
            onClick={() => setAuthPanelMode('login')}
            style={{
              padding: '8px 12px',
              background: '#21262d',
              border: '1px solid #30363d',
              color: '#e2e8f0',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            SIGN IN
          </button>
          <button
            onClick={() => setAuthPanelMode('signup')}
            style={{
              padding: '8px 12px',
              background: '#238636',
              border: '1px solid #2ea043',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            REGISTER
          </button>
        </div>
      );
    }

    // Auth panel open - show login/signup form
    return (
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: 'rgba(13, 17, 23, 0.72)',
        border: '1px solid rgba(33, 38, 45, 0.8)',
        padding: '16px',
        width: '280px',
        zIndex: 100,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '6px',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }}>
            {authPanelMode === 'login' ? 'Sign In' : 'Create Account'}
          </span>
          <button
            onClick={() => setAuthPanelMode('closed')}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {auth.error && (
          <div style={{
            padding: '8px 12px',
            background: '#3d2d2d',
            border: '1px solid #8b4545',
            color: '#ff6b6b',
            fontSize: '11px',
            marginBottom: '12px',
            borderRadius: '4px',
          }}>
            <div>{auth.error}</div>
            {auth.error.toLowerCase().includes('confirm your email') && authPanelMode === 'login' && (
              <button
                onClick={async () => {
                  const result = await auth.resendConfirmationEmail(email);
                  if (result.success) {
                    showToast('Confirmation email sent! Check your inbox.', 'success');
                  } else {
                    showToast(result.error || 'Failed to resend confirmation email', 'error');
                  }
                }}
                style={{
                  display: 'block',
                  marginTop: '8px',
                  background: '#58a6ff',
                  border: 'none',
                  color: '#0d1117',
                  padding: '4px 8px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  borderRadius: '3px',
                  fontWeight: 500,
                }}
              >
                Resend Confirmation Email
              </button>
            )}
          </div>
        )}

        {shouldShowOAuthButtons && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
            <button
              onClick={handleGoogleSignIn}
              disabled={auth.isLoading}
              style={{
                width: '100%',
                padding: '10px',
                background: '#fff',
                border: '1px solid #d0d7de',
                color: '#24292f',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <button
              onClick={handleDiscordSignIn}
              disabled={auth.isLoading}
              style={{
                width: '100%',
                padding: '10px',
                background: '#5865F2',
                border: '1px solid #4752C4',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Continue with Discord
            </button>
          </div>
        )}

        {shouldShowOAuthButtons && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
          }}>
            <div style={{ flex: 1, height: '1px', background: '#21262d' }} />
            <span style={{ fontSize: '10px', color: '#8b949e' }}>or use email</span>
            <div style={{ flex: 1, height: '1px', background: '#21262d' }} />
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleAuthSubmit}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={{
                width: '100%',
                padding: '8px',
                background: '#0d1117',
                border: '1px solid #30363d',
                color: '#e2e8f0',
                fontSize: '12px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: '#8b949e', marginBottom: '4px' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              minLength={6}
              required
              style={{
                width: '100%',
                padding: '8px',
                background: '#0d1117',
                border: '1px solid #30363d',
                color: '#e2e8f0',
                fontSize: '12px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            type="submit"
            disabled={auth.isLoading}
            style={{
              width: '100%',
              padding: '10px',
              background: authPanelMode === 'signup' ? '#238636' : '#21262d',
              border: `1px solid ${authPanelMode === 'signup' ? '#2ea043' : '#30363d'}`,
              color: '#fff',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {(auth.isLoading || isSigningIn) ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <LoadingSpinner size={14} color="#fff" />
                {authPanelMode === 'login' ? 'Signing in...' : 'Creating account...'}
              </span>
            ) : (
              authPanelMode === 'login' ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <button
            onClick={() => setAuthPanelMode(authPanelMode === 'login' ? 'signup' : 'login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#58a6ff',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            {authPanelMode === 'login' ? 'Need an account? Register' : 'Have an account? Sign in'}
          </button>
        </div>
      </div>
    );
  };

  // Character selection for authenticated users
  const renderCharacterSelection = (): React.ReactElement => (
    <div className="login-screen" style={{ background: '#0b1220' }}>
      <LoginWorldCanvas width={typeof window !== 'undefined' ? window.innerWidth : 1024} height={typeof window !== 'undefined' ? window.innerHeight : 768} />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {renderAuthPanel()}
      
      <div className="login-container" style={{ 
        maxWidth: '500px',
        background: 'rgba(13, 17, 23, 0.72)',
        border: '1px solid rgba(33, 38, 45, 0.8)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '6px',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
        padding: '24px',
      }}>
        <h1 className="font-curse-48">
          {serverStatus?.status === 'online' ? (
            <>
              GRAND <span className="status-on" style={{ textShadow: '0 0 6px rgba(34, 197, 94, 0.5), 0 0 12px rgba(34, 197, 94, 0.4)' }}>ON</span>LINE
            </>
          ) : (
            <>
              GRAND <span className="status-off" style={{ textShadow: '0 0 6px rgba(248, 81, 73, 0.5), 0 0 12px rgba(248, 81, 73, 0.4)' }}>OFF</span>LINE
            </>
          )}
        </h1>
  
        <div className="form-group">
          {!isCreatingCharacter && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', minHeight: auth.isCharactersLoading ? '120px' : 'auto' }}>
              {auth.isCharactersLoading ? (
                // Show skeleton loaders while characters load
                <>
                  <CharacterSkeleton />
                  <CharacterSkeleton />
                </>
              ) : auth.characters.map((char, index) => (
                <div
                  key={char.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: playingCharId === char.id ? '#1e3a5f' : '#111827',
                    border: `1px solid ${playingCharId === char.id ? '#3b82f6' : '#1f2937'}`,
                    cursor: playingCharId ? 'wait' : 'pointer',
                    opacity: (playingCharId && playingCharId !== char.id) ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    animation: `loginSlideIn 0.3s ease-out ${index * 0.05}s both`,
                    borderRadius: '8px',
                  }}
                  onClick={() => !playingCharId && handlePlayCharacter(char)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: char.shipColor,
                        border: '2px solid #fff',
                        animation: playingCharId === char.id ? 'loginGlow 1s ease-in-out infinite' : 'none',
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{char.name}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        Lv.{char.level} {char.characterType} • {char.berries.toLocaleString()} ₱
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {playingCharId === char.id ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#60a5fa' }}>
                        <LoadingSpinner size={14} color="#60a5fa" />
                        Loading...
                      </span>
                    ) : deletingCharId === char.id ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#f87171' }}>
                        <LoadingSpinner size={14} color="#f87171" />
                        Deleting...
                      </span>
                    ) : deleteConfirmId === char.id ? (
                      <>
                        <span style={{ fontSize: '11px', color: '#f87171', animation: 'loginPulse 1s ease-in-out infinite' }}>Delete?</span>
                        <button
                          style={{
                            padding: '6px 10px',
                            fontSize: '11px',
                            background: '#dc2626',
                            border: '1px solid #ef4444',
                            color: '#fff',
                            cursor: 'pointer',
                            animation: 'loginShake 0.3s ease-out',
                            borderRadius: '6px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCharacter(char.id, char.name);
                          }}
                        >
                          YES
                        </button>
                        <button
                          style={{
                            padding: '6px 10px',
                            fontSize: '11px',
                            background: '#374151',
                            border: '1px solid #4b5563',
                            color: '#e2e8f0',
                            cursor: 'pointer',
                            borderRadius: '6px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(null);
                          }}
                        >
                          NO
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="primary"
                          style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayCharacter(char);
                          }}
                        >
                          PLAY
                        </button>
                        <button
                          style={{
                            padding: '6px 10px',
                            fontSize: '11px',
                            background: '#1f2937',
                            border: '1px solid #374151',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            borderRadius: '6px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(char.id);
                          }}
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Show loading indicator if auth is still initializing */}
          {auth.isLoading && !auth.isCharactersLoading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              color: '#8b949e',
              fontSize: '12px',
            }}>
              <LoadingSpinner size={14} color="#8b949e" />
              Loading...
            </div>
          )}

          {isCreatingCharacter ? (
            <form onSubmit={handleCreateCharacter} style={{ marginTop: '12px' }}>
              <div className="form-group">
                <label htmlFor="char-name">Character Name</label>
                <input
                  id="char-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter character name..."
                  maxLength={20}
                  autoFocus
                  style={{ borderRadius: '6px' }}
                />
                
              </div>

              <div className="form-group">
                <label>Select Your Calling</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                  marginTop: '12px',
                }}>
                  {CHARACTERS.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => setCharacter(c.id)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: character === c.id ? '2px solid #58a6ff' : '1px solid #30363d',
                        background: character === c.id ? '#1e3a5f' : '#111827',
                        transition: 'all 0.2s ease',
                        transform: character === c.id ? 'scale(1.02)' : 'scale(1)',
                      }}
                    >
                      {/* Portrait */}
                      <div style={{
                        width: '100%',
                        height: '120px',
                        background: '#0d1117',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        <img 
                          src={c.portraitPath} 
                          alt={c.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect fill="%230f172a" width="120" height="120"/%3E%3C/svg%3E';
                          }}
                        />
                      </div>

                      {/* Label */}
                      <div style={{ padding: '10px', textAlign: 'center' }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#e2e8f0',
                        }}>
                          {c.name}
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: '#8b949e',
                          marginTop: '4px',
                        }}>
                          {c.startingBonuses?.berries && `+${c.startingBonuses.berries} ₱`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Ship Color</label>
                <div className="color-palette">
                  {SHIP_COLORS.map((color) => (
                    <div
                      key={color.id}
                      className={`color-swatch ${shipColor === color.hex ? 'selected' : ''}`}
                      style={{ backgroundColor: color.hex }}
                      onClick={() => setShipColor(color.hex)}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {nameCheckError && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#f87171' }}>
                  {nameCheckError}
                </div>
              )}
              {nameAvailable === true && !isCheckingName && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#2ecc71' }}>
                  Name available
                </div>
              )}
              {isCheckingName && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#58a6ff' }}>
                  Checking name...
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button 
                  type="submit" 
                  className="primary" 
                  disabled={!name.trim() || nameAvailable === false || isCheckingName || isSubmittingCharacter}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isSubmittingCharacter ? 0.7 : 1,
                    borderRadius: '6px',
                  }}
                >
                  {isSubmittingCharacter && <LoadingSpinner size={14} color="#fff" />}
                  {isSubmittingCharacter
                    ? 'CREATING...'
                    : !name.trim()
                      ? 'ENTER NAME'
                      : nameAvailable === false
                        ? 'NAME TAKEN'
                        : isCheckingName
                          ? 'CHECKING...'
                          : 'CREATE CHARACTER'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreatingCharacter(false)}
                  disabled={isSubmittingCharacter}
                  style={{ background: '#374151', border: '1px solid #4b5563', opacity: isSubmittingCharacter ? 0.5 : 1, borderRadius: '6px' }}
                >
                  CANCEL
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setIsCreatingCharacter(true)}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                background: '#1f2937',
                border: '1px dashed #4b5563',
                color: '#94a3b8',
                cursor: 'pointer',
                borderRadius: '8px',
              }}
            >
              + CREATE NEW CHARACTER
            </button>
          )}
        </div>

      </div>

      {/* Discord Server Prompt - positioned above footer */}
      <div style={{
        position: 'fixed',
        bottom: '80px',
        right: '20px',
        zIndex: 45,
        pointerEvents: 'auto',
      }}>
        <div style={{
          background: 'rgba(88, 101, 242, 0.85)',
          border: '1px solid rgba(114, 137, 218, 0.9)',
          padding: '12px 16px',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'center',
        }}>
          <div style={{
            color: '#fff',
            fontSize: '13px',
            fontWeight: 'bold',
            fontFamily: 'var(--font-mono)',
          }}>
            JOIN OUR DISCORD
          </div>
          <a 
            href="https://discord.com/invite/tvbEDUZvvB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              padding: '8px 16px',
              background: '#fff',
              color: '#5865F2',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: 'var(--font-mono)',
              textDecoration: 'none',
              display: 'block',
              textAlign: 'center',
            }}
          >
            OPEN DISCORD
          </a>
        </div>
      </div>

      <FooterLinks 
        onShowWhatIs={() => setShowWhatIsModal(true)}
        onShowDailyRewards={() => {}}
        isLoggedIn={!!auth.user}
        serverStatus={serverStatus}
      />
      {showWhatIsModal && (
        <WhatIsGrandLineModal onClose={() => setShowWhatIsModal(false)} />
      )}
      {/* Username Claim Modal (authenticated users) */}
      {showUsernameClaimModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2500,
          fontFamily: 'var(--font-mono)',
        }}>
          <div style={{
            background: 'rgba(13, 17, 23, 0.78)',
            border: '2px solid rgba(33, 38, 45, 0.85)',
            width: '100%',
            maxWidth: '400px',
            padding: '24px',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: '6px',
            boxShadow: '0 16px 36px rgba(0, 0, 0, 0.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: '16px', fontFamily: 'var(--font-mono)' }}>Claim Your Username</h2>
              <CloseButton onClick={() => setShowUsernameClaimModal(false)} />
            </div>
            
            <p style={{ color: '#8b949e', fontSize: '12px', marginBottom: '16px', lineHeight: 1.5 }}>
              Choose a unique username for your account. This will be displayed on your ships and visible to other players.
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: '#8b949e', marginBottom: '6px' }}>
                Username
              </label>
              <input
                type="text"
                value={claimUsername}
                onChange={(e) => setClaimUsername(e.target.value)}
                placeholder="Enter username..."
                maxLength={20}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#161b22',
                  border: `1px solid ${usernameError ? '#f85149' : usernameAvailable === true ? '#2ea043' : usernameAvailable === false ? '#f85149' : '#30363d'}`,
                  color: '#e2e8f0',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
              
              <div style={{ marginTop: '8px', minHeight: '20px' }}>
                {isCheckingUsername && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8b949e' }}>
                    <LoadingSpinner size={12} color="#8b949e" />
                    Checking availability...
                  </div>
                )}
                {!isCheckingUsername && usernameError && (
                  <div style={{ fontSize: '11px', color: '#f85149' }}>
                    {usernameError}
                  </div>
                )}
                {!isCheckingUsername && !usernameError && usernameAvailable === true && (
                  <div style={{ fontSize: '11px', color: '#2ea043' }}>
                    Username is available
                  </div>
                )}
                {!isCheckingUsername && !usernameError && usernameAvailable === false && (
                  <div style={{ fontSize: '11px', color: '#f85149' }}>
                    Username is already taken
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowUsernameClaimModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                SKIP FOR NOW
              </button>
              <button
                onClick={handleClaimUsername}
                disabled={!usernameAvailable || isClaimingUsername}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: usernameAvailable ? '#238636' : '#21262d',
                  border: `1px solid ${usernameAvailable ? '#2ea043' : '#30363d'}`,
                  color: '#fff',
                  cursor: usernameAvailable && !isClaimingUsername ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: 600,
                  opacity: usernameAvailable && !isClaimingUsername ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                {isClaimingUsername && <LoadingSpinner size={12} color="#fff" />}
                {isClaimingUsername ? 'CLAIMING...' : 'CLAIM USERNAME'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Show full-page loading state during initial auth check
  if (auth.isLoading && !auth.user && auth.isAuthEnabled) {
    return (
      <div className="login-screen">
        <div className="login-container" style={{ textAlign: 'center' }}>
          <h1 className="font-curse-48">
            {serverStatus?.status === 'online' ? (
              <>
                GRAND <span className="status-on">ON</span>LINE
              </>
            ) : (
              <>
                GRAND <span className="status-off">OFF</span>LINE
              </>
            )}
          </h1>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            marginTop: '32px',
          }}>
            <LoadingSpinner size={32} color="#58a6ff" />
            <p style={{ color: '#8b949e', fontSize: '13px' }}>Checking authentication...</p>
          </div>
        </div>
        <FooterLinks 
          onShowWhatIs={() => setShowWhatIsModal(true)}
          onShowDailyRewards={() => {}}
          isLoggedIn={!!auth.user}
        />
        {showWhatIsModal && (
          <WhatIsGrandLineModal onClose={() => setShowWhatIsModal(false)} />
        )}
      </div>
    );
  }

  // If user is authenticated, show character selection
  if (auth.user) {
    return renderCharacterSelection();
  }

  // Default: Guest mode with auth panel in top-right
  return (
    <div className="login-screen" style={{ background: '#0b1220' }}>
      <LoginWorldCanvas width={typeof window !== 'undefined' ? window.innerWidth : 1024} height={typeof window !== 'undefined' ? window.innerHeight : 768} />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {renderAuthPanel()}
      
      {/* Discord Server Prompt */}
      <div className="discord-prompt">
        <div className="discord-prompt-content">
          <div className="discord-prompt-header">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 0-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .078.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            <span>Join Our Discord</span>
          </div>
          <a 
            href="https://discord.com/invite/tvbEDUZvvB" 
            target="_blank" 
            rel="noopener noreferrer"
            className="discord-prompt-button"
          >
            DISCORD SERVER
          </a>
        </div>
      </div>
      
      <div className="login-container">
        <h1 className="font-curse-48">
          {serverStatus?.status === 'online' ? (
            <>
              GRAND <span className="status-on">ON</span>LINE
            </>
          ) : (
            <>
              GRAND <span className="status-off">OFF</span>LINE
            </>
          )}
        </h1>

        <p className="subtitle text-muted font-lookout-21">
          Set sail in the persistent sandbox sea-faring MMO.
        </p>

        {/* Server status indicator */}
        <div style={{ minHeight: '24px', marginBottom: '8px' }}>
          {serverStatus && (
            <div className="server-status" style={{ animation: 'loginFadeIn 0.3s ease-out' }}>
              <span className={`status-dot ${serverStatus.status === 'online' ? 'online' : 'offline'}`} />
              <span className="status-text">
                {serverStatus.status === 'offline'
                  ? 'Server offline'
                  : `${serverStatus.explorerCount || 0} explorers active`}
              </span>
            </div>
          )}
        </div>

        <form className="login-form" onSubmit={handleGuestSubmit}>
          <div className="form-group">
            <label htmlFor="name">Ship Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your ship's name..."
              maxLength={20}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Select Your Calling</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginTop: '12px',
            }}>
              {CHARACTERS.map((c) => (
                <div
                  key={c.id}
                  onClick={() => setCharacter(c.id)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    border: character === c.id ? '2px solid #58a6ff' : '1px solid #30363d',
                    background: character === c.id ? '#1e3a5f' : '#111827',
                    transition: 'all 0.2s ease',
                    transform: character === c.id ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  {/* Portrait */}
                  <div style={{
                    width: '100%',
                    height: '120px',
                    background: '#0d1117',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    <img 
                      src={c.portraitPath} 
                      alt={c.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect fill="%230f172a" width="120" height="120"/%3E%3C/svg%3E';
                      }}
                    />
                  </div>

                  {/* Label */}
                  <div style={{ padding: '10px', textAlign: 'center' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#e2e8f0',
                    }}>
                      {c.name}
                    </div>
                    <div style={{
                      fontSize: '10px',
                      color: '#8b949e',
                      marginTop: '4px',
                    }}>
                      {c.startingBonuses?.berries && `+${c.startingBonuses.berries} ₱`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Ship Color</label>
            <div className="color-palette">
              {SHIP_COLORS.map((color) => (
                <div
                  key={color.id}
                  className={`color-swatch ${shipColor === color.hex ? 'selected' : ''}`}
                  style={{ backgroundColor: color.hex }}
                  onClick={() => setShipColor(color.hex)}
                  title={color.name}
                />
              ))}
            </div>
            <div className="color-preview">
              <div 
                className="preview-ship"
                style={{ backgroundColor: shipColor }}
              />
              <span className="preview-label">Your ship, the <span
                style={{ color: shipColor }}
              >{name ? name : 'Currently Unnamed'}</span>.</span>
            </div>
          </div>

          <button type="submit" className="primary" disabled={!name.trim()}>
            SET SAIL
          </button>
        </form>

        {auth.isAuthEnabled && (
          <p style={{ marginTop: '12px', fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>
            Sign in to save your progress across sessions
          </p>
        )}
      </div>

      <FooterLinks 
        onShowWhatIs={() => setShowWhatIsModal(true)}
        onShowDailyRewards={() => {}}
        isLoggedIn={!!auth.user}
        serverStatus={serverStatus}
      />
      {showWhatIsModal && (
        <WhatIsGrandLineModal onClose={() => setShowWhatIsModal(false)} />
      )}
      
      {/* Username Claim Modal */}
      {showUsernameClaimModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2500,
          fontFamily: 'var(--font-mono)',
        }}>
          <div style={{
            background: 'rgba(13, 17, 23, 0.78)',
            border: '2px solid rgba(33, 38, 45, 0.85)',
            width: '100%',
            maxWidth: '400px',
            padding: '24px',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 16px 36px rgba(0, 0, 0, 0.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: '16px', fontFamily: 'var(--font-mono)' }}>Claim Your Username</h2>
              <button
                onClick={() => setShowUsernameClaimModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8b949e',
                  fontSize: '20px',
                  cursor: 'pointer',
                }}
              >×</button>
            </div>
            
            <p style={{ color: '#8b949e', fontSize: '12px', marginBottom: '16px', lineHeight: 1.5 }}>
              Choose a unique username for your account. This will be displayed on your ships and visible to other players.
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: '#8b949e', marginBottom: '6px' }}>
                Username
              </label>
              <input
                type="text"
                value={claimUsername}
                onChange={(e) => setClaimUsername(e.target.value)}
                placeholder="Enter username..."
                maxLength={20}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#161b22',
                  border: `1px solid ${usernameError ? '#f85149' : usernameAvailable === true ? '#2ea043' : usernameAvailable === false ? '#f85149' : '#30363d'}`,
                  color: '#e2e8f0',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
              
              {/* Status indicator */}
              <div style={{ marginTop: '8px', minHeight: '20px' }}>
                {isCheckingUsername && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8b949e' }}>
                    <LoadingSpinner size={12} color="#8b949e" />
                    Checking availability...
                  </div>
                )}
                {!isCheckingUsername && usernameError && (
                  <div style={{ fontSize: '11px', color: '#f85149' }}>
                    {usernameError}
                  </div>
                )}
                {!isCheckingUsername && !usernameError && usernameAvailable === true && (
                  <div style={{ fontSize: '11px', color: '#2ea043' }}>
                    Username is available
                  </div>
                )}
                {!isCheckingUsername && !usernameError && usernameAvailable === false && (
                  <div style={{ fontSize: '11px', color: '#f85149' }}>
                    Username is already taken
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowUsernameClaimModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                SKIP FOR NOW
              </button>
              <button
                onClick={handleClaimUsername}
                disabled={!usernameAvailable || isClaimingUsername}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: usernameAvailable ? '#238636' : '#21262d',
                  border: `1px solid ${usernameAvailable ? '#2ea043' : '#30363d'}`,
                  color: '#fff',
                  cursor: usernameAvailable && !isClaimingUsername ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: 600,
                  opacity: usernameAvailable && !isClaimingUsername ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                {isClaimingUsername && <LoadingSpinner size={12} color="#fff" />}
                {isClaimingUsername ? 'CLAIMING...' : 'CLAIM USERNAME'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Ocean Selection Modal */}
      {showOceanSelection && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          fontFamily: 'var(--font-mono)',
        }}>
          <div style={{
            background: 'rgba(13, 17, 23, 0.78)',
            border: '2px solid rgba(33, 38, 45, 0.85)',
            width: '100%',
            maxWidth: '600px',
            padding: '24px',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: '6px',
            boxShadow: '0 16px 36px rgba(0, 0, 0, 0.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: '18px' }}>Choose Your Starting Ocean</h2>
              <CloseButton onClick={() => setShowOceanSelection(false)} />
            </div>
            
            <p style={{ color: '#8b949e', fontSize: '12px', marginBottom: '20px' }}>
              Select where you want to begin your journey. Each ocean has different challenges and opportunities.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
              {STARTING_OCEANS.map((ocean) => (
                <div
                  key={ocean.id}
                  onClick={() => setStartingOcean(ocean.id)}
                  style={{
                    padding: '16px',
                    background: startingOcean === ocean.id ? '#1a1a2e' : '#161b22',
                    border: `2px solid ${startingOcean === ocean.id ? ocean.color : '#30363d'}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    transform: startingOcean === ocean.id ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{ocean.name}</span>
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#0d1117',
                      background: ocean.color,
                      padding: '2px 8px',
                      fontWeight: 600,
                    }}>{ocean.difficulty}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#8b949e', lineHeight: 1.4 }}>{ocean.description}</div>
                </div>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowOceanSelection(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                BACK
              </button>
              <button
                onClick={handleOceanConfirm}
                style={{
                  flex: 2,
                  padding: '12px',
                  background: '#238636',
                  border: '1px solid #2ea043',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                EMBARK TO {STARTING_OCEANS.find(o => o.id === startingOcean)?.name.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
